/**
 * Turns dummy time into the handful of signals the world animates from.
 *
 * This is a **stand-in for the audio layer**, not gameplay. When the runtime is
 * wired up, `breath` and `swell` stay as they are, `pulse` comes from `BeatGrid`
 * instead of a hardcoded tempo, and `time` comes from `MusicClock`. Keeping the
 * signal set explicit now means the views already consume the interface they will
 * eventually be driven through, so none of them has to change.
 *
 * The periods below are mutually irrational on purpose. Anything that shares a
 * common multiple eventually resynchronises, and a visible loop is what makes a
 * generated scene read as a screensaver rather than as a place.
 */
export interface WorldSignals {
  /** Seconds since the world started. */
  readonly time: number;
  /** Slow inhale/exhale, 0..1. */
  readonly breath: number;
  /** Sharp attack, exponential decay, 0..1. Stands in for a beat. */
  readonly pulse: number;
  /** Very slow tidal swell, -1..1. */
  readonly swell: number;
  /** Multiplier applied to bloom strength. */
  readonly bloom: number;
}

export interface ConductorOptions {
  /** Seconds between dummy pulses. 0.72 s is a shade over 83 BPM. */
  readonly pulsePeriod?: number;
  readonly breathPeriod?: number;
}

export class Conductor {
  private readonly pulsePeriod: number;
  private readonly breathPeriod: number;

  constructor(options: ConductorOptions = {}) {
    this.pulsePeriod = options.pulsePeriod ?? 0.72;
    this.breathPeriod = options.breathPeriod ?? 13.7;
  }

  sample(time: number): WorldSignals {
    // Two detuned components, so the breath is never quite the same shape twice.
    const primary = Math.sin((time / this.breathPeriod) * Math.PI * 2);
    const secondary = Math.sin((time / (this.breathPeriod * 2.61)) * Math.PI * 2);
    const breath = 0.5 + 0.5 * (primary * 0.72 + secondary * 0.28);

    // Percussive envelope: instant attack, cubic decay across the beat.
    const beatPhase = (time % this.pulsePeriod) / this.pulsePeriod;
    const decay = (1 - beatPhase) ** 3;
    // Accent every fourth beat so there is a bar, not just a tick.
    const bar = Math.floor(time / this.pulsePeriod) % 4;
    const pulse = decay * (bar === 0 ? 1 : 0.55);

    const swell = Math.sin((time / 47.3) * Math.PI * 2);

    // Bloom follows the breath, with a small kick on the accent. Modulating the
    // post chain rather than the emitters makes the whole frame swell together.
    const bloom = 0.88 + breath * 0.22 + pulse * 0.1;

    return { time, breath, pulse, swell, bloom };
  }
}
